import { useEffect, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { FileDown, X, Maximize2, ChevronLeft, ChevronRight } from "lucide-react";
import Reveal from "@/components/landing/Reveal";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Referenzen() {
  const [missions, setMissions] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [showcase, setShowcase] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const cards = showcase.filter((s) => s.type === "card");
  const docs = showcase.filter((s) => s.type === "doc");

  useEffect(() => {
    axios
      .get(`${BACKEND}/api/missions`)
      .then((res) => setMissions(res.data))
      .catch(() => {});
    axios
      .get(`${BACKEND}/api/documents`)
      .then((res) => setDocuments(res.data))
      .catch(() => {});
    axios
      .get(`${BACKEND}/api/showcase`)
      .then((res) => setShowcase(res.data))
      .catch(() => {});
    axios
      .get(`${BACKEND}/api/public/site-images?slot=referenz`)
      .then((res) => setGallery(res.data))
      .catch(() => {});
  }, []);

  const openLightbox = (images, index = 0) => setLightbox({ images, index });
  const stepLightbox = (dir) =>
    setLightbox((lb) => {
      if (!lb) return lb;
      const next = (lb.index + dir + lb.images.length) % lb.images.length;
      return { ...lb, index: next };
    });

  return (
    <section id="referenzen" data-testid="referenzen-section" className="relative">
      <div className="border-b border-white/10 px-6 py-20 lg:px-16 lg:py-28">
        <Reveal>
          <p className="font-tac text-xs tracking-[0.3em] text-[#F5B301]">REFERENZEN & EINSATZBERICHTE</p>
          <h2 className="mt-6 font-display text-4xl font-black uppercase leading-[0.9] tracking-tighter sm:text-5xl lg:text-7xl">
            Aus der Luft.
            <br />
            <span className="text-stroke">Abgeschlossen.</span>
          </h2>
          <p className="mt-6 max-w-2xl font-tac text-sm leading-relaxed text-[#A1A1AA]">
            Ausgewählte Referenzen und Einsatzberichte aus der Praxis — Drohnen-Inspektion von Dächern,
            Brücken und Industrieanlagen, RTK-Vermessung sowie Thermografie und Lagedokumentation.
            Jeder Einsatz sauber erfasst, aufbereitet und für unsere Kunden nachvollziehbar dokumentiert.
          </p>
        </Reveal>
      </div>

      {gallery.length > 0 && (
        <section data-testid="referenz-gallery" className="border-b border-white/10 px-6 py-16 lg:px-16 lg:py-24">
          <Reveal>
            <p className="font-tac text-xs tracking-[0.3em] text-[#F5B301]">BILDGALERIE</p>
            <h3 className="mt-6 max-w-3xl font-display text-3xl font-black uppercase leading-[0.95] tracking-tighter sm:text-4xl lg:text-5xl">
              Ausgewählte <span className="text-stroke">Aufnahmen.</span>
            </h3>
          </Reveal>
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {gallery.map((img, i) => (
              <Reveal key={img.id} delay={i * 0.06}>
                <button
                  type="button"
                  data-testid={`referenz-gallery-item-${i}`}
                  onClick={() => openLightbox(gallery.map((g) => `${BACKEND}${g.url}`), i)}
                  className="group relative block aspect-[4/3] w-full overflow-hidden border border-white/10"
                >
                  <img
                    src={`${BACKEND}${img.url}`}
                    alt={img.alt || img.title || "Referenzaufnahme aus einem Drohneneinsatz"}
                    loading="lazy"
                    className="h-full w-full object-cover brightness-[0.9] contrast-110 transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-all duration-300 group-hover:bg-black/40 group-hover:opacity-100">
                    <Maximize2 className="h-7 w-7 text-[#F5B301]" />
                  </span>
                  <span className="pointer-events-none absolute left-3 top-3 h-4 w-4 border-l-2 border-t-2 border-[#F5B301]" />
                  {img.title && (
                    <p className="pointer-events-none absolute bottom-3 left-3 font-tac text-[9px] tracking-[0.3em] text-white/70">
                      {img.title}
                    </p>
                  )}
                </button>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      <article data-testid="referenz-hagen" className="grid border-b border-white/10 lg:grid-cols-12">
        <div className="relative border-white/10 max-lg:border-b lg:col-span-7 lg:border-r">
          <div className="group relative aspect-[16/10] overflow-hidden">
            <img
              data-testid="referenz-hagen-image"
              src="/assets/roof-inspection.jpg"
              alt="Drohne bei der laufenden Dachinspektion eines Mehrfamilienhauses in Hagen"
              loading="lazy"
              className="h-full w-full object-cover brightness-[0.85] contrast-110 grayscale-[30%] transition-transform duration-700 ease-out group-hover:scale-105"
            />
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_50%_45%,transparent_30%,rgba(9,9,11,0.7)_100%)]" />
            <span className="absolute right-4 top-4 flex items-center gap-1.5 bg-black/75 px-2.5 py-1 font-tac text-[9px] tracking-[0.25em] text-[#F5B301]">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#F5B301]" />
              AUFTRAG LÄUFT
            </span>
            <span className="absolute left-4 top-4 h-5 w-5 border-l-2 border-t-2 border-[#F5B301]" />
            <span className="absolute bottom-4 right-4 h-5 w-5 border-b-2 border-r-2 border-[#F5B301]" />
            <p className="absolute bottom-4 left-4 font-tac text-[10px] tracking-[0.3em] text-white/70">
              EINSATZ 2026-000062 // HAGEN
            </p>
          </div>
        </div>
        <div className="flex flex-col justify-center px-6 py-14 lg:col-span-5 lg:px-16">
          <Reveal>
            <p className="font-tac text-xs tracking-[0.3em] text-[#F5B301]">EINSATZBERICHT</p>
            <h3 className="mt-5 font-display text-3xl font-black uppercase leading-[0.95] tracking-tighter sm:text-4xl">
              Dachinspektion
              <br />
              Mehrfamilienhaus
            </h3>
            <p className="mt-6 max-w-md font-tac text-sm leading-relaxed text-[#A1A1AA]">
              Sturmschaden-Prüfung per Drohne: Bewuchs, Moos und Verschmutzung
              auf dem Dach werden aktuell lückenlos dokumentiert — der Auftrag
              ist noch in Bearbeitung, eine Empfehlung zur Detailprüfung folgt.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-4 border-t border-white/10 pt-6">
              {[
                ["ORT", "Hagen"],
                ["JAHR", "2026"],
                ["STATUS", "In Bearbeitung"],
              ].map(([label, value]) => (
                <div key={label}>
                  <p className="font-tac text-[9px] tracking-[0.3em] text-white/40">{label}</p>
                  <p className="mt-1.5 font-tac text-xs font-bold uppercase tracking-wider text-white">{value}</p>
                </div>
              ))}
            </div>
            <p className="mt-8 font-tac text-[10px] leading-relaxed tracking-[0.15em] text-white/40">
              AUS DATENSCHUTZGRÜNDEN WERDEN EINSATZBERICHTE NICHT ÖFFENTLICH
              BEREITGESTELLT — AUF ANFRAGE ERHÄLTST DU EINE ANONYMISIERTE VERSION.
            </p>
          </Reveal>
        </div>
      </article>

      <article data-testid="referenz-lageaufnahme" className="grid border-b border-white/10 lg:grid-cols-12">
        <div className="flex flex-col justify-center px-6 py-14 lg:col-span-5 lg:px-16 lg:order-1">
          <Reveal>
            <p className="font-tac text-xs tracking-[0.3em] text-[#F5B301]">LAGEAUFNAHME</p>
            <h3 className="mt-5 font-display text-3xl font-black uppercase leading-[0.95] tracking-tighter sm:text-4xl">
              GPS-
              <br />
              Dokumentation
            </h3>
            <p className="mt-6 max-w-md font-tac text-sm leading-relaxed text-[#A1A1AA]">
              Jeder Einsatz wird lagegenau dokumentiert: Start- und Landeplätze,
              Referenz- und Kontrollpunkte werden per GPS erfasst und in einer
              Kartenübersicht festgehalten — nachvollziehbar und revisionssicher.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-4 border-t border-white/10 pt-6">
              {[
                ["EINSATZ", "2026-000050"],
                ["PUNKTE", "3 erfasst"],
                ["QUELLE", "WGS84"],
              ].map(([label, value]) => (
                <div key={label}>
                  <p className="font-tac text-[9px] tracking-[0.3em] text-white/40">{label}</p>
                  <p className="mt-1.5 font-tac text-xs font-bold uppercase tracking-wider text-white">{value}</p>
                </div>
              ))}
            </div>
            <p className="mt-8 font-tac text-[10px] leading-relaxed tracking-[0.15em] text-white/40">
              KUNDEN- UND PERSONENBEZOGENE DATEN WURDEN AUS DATENSCHUTZGRÜNDEN
              UNKENNTLICH GEMACHT — DIE VOLLSTÄNDIGE DOKUMENTATION ERHÄLTST DU
              AUF ANFRAGE.
            </p>
          </Reveal>
        </div>
        <div className="relative border-white/10 max-lg:border-t lg:col-span-7 lg:border-l lg:order-2">
          <div className="relative flex items-center justify-center bg-[#0d0d10] p-6 lg:p-10">
            <button
              type="button"
              data-testid="referenz-lageaufnahme-open"
              onClick={() => openLightbox(["/assets/lage-2026-000050.jpg"])}
              className="group/doc relative w-full max-w-md overflow-hidden border border-white/10 shadow-2xl transition-transform duration-500 hover:scale-[1.02]"
            >
              <img
                data-testid="referenz-lageaufnahme-image"
                src="/assets/lage-2026-000050.jpg"
                alt="Anonymisierte GPS-Lagedokumentation eines Drohneneinsatzes mit Kartenübersicht und erfassten Geo-Punkten"
                loading="lazy"
                className="h-full w-full object-contain"
              />
              <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-all duration-300 group-hover/doc:bg-black/40 group-hover/doc:opacity-100">
                <Maximize2 className="h-8 w-8 text-[#F5B301]" />
              </span>
            </button>
            <span className="pointer-events-none absolute left-4 top-4 h-5 w-5 border-l-2 border-t-2 border-[#F5B301]" />
            <span className="pointer-events-none absolute bottom-4 right-4 h-5 w-5 border-b-2 border-r-2 border-[#F5B301]" />
            <p className="pointer-events-none absolute bottom-4 left-4 font-tac text-[10px] tracking-[0.3em] text-white/50">
              GEO-PUNKTE // ANONYMISIERT
            </p>
          </div>
        </div>
      </article>

      <section data-testid="referenz-dokumentation" className="border-b border-white/10 px-6 py-16 lg:px-16 lg:py-24">
        <Reveal>
          <p className="font-tac text-xs tracking-[0.3em] text-[#F5B301]">EINSATZDOKUMENTATION</p>
          <h3 className="mt-6 max-w-3xl font-display text-3xl font-black uppercase leading-[0.95] tracking-tighter sm:text-4xl lg:text-5xl">
            Auf Kundenwunsch <span className="text-stroke">angepasst.</span>
          </h3>
          <p className="mt-6 max-w-2xl font-tac text-sm leading-relaxed text-[#A1A1AA]">
            Wir passen unsere Dokumentation auf Kundenwunsch an, um den jeweiligen
            Profilen und Anforderungen zu entsprechen — von Einsatz- und Notfallplan
            über Anforderungsprofil und Anfahrtsbericht bis zur Aufstiegsgenehmigung.
            Alle personenbezogenen Daten sind aus Datenschutzgründen unkenntlich gemacht.
          </p>
        </Reveal>

        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {docs.map((doc, i) => (
            <Reveal key={doc.id} delay={i * 0.08}>
              <button
                type="button"
                data-testid={`doc-card-${doc.id}`}
                onClick={() => openLightbox([`${BACKEND}/api/showcase-image/${doc.image_path}`])}
                className="group/doc block w-full text-left"
              >
                <div className="relative aspect-[3/4] overflow-hidden border border-white/10 bg-white">
                  <img
                    src={`${BACKEND}/api/showcase-image/${doc.image_path}`}
                    alt={`Anonymisiertes Dokument: ${doc.title}`}
                    loading="lazy"
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover/doc:scale-[1.03]"
                  />
                  <span className="absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-all duration-300 group-hover/doc:bg-black/40 group-hover/doc:opacity-100">
                    <Maximize2 className="h-7 w-7 text-[#F5B301]" />
                  </span>
                  <span className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 border-l-2 border-t-2 border-[#F5B301]" />
                </div>
                <p className="mt-4 font-display text-lg font-black uppercase tracking-tighter text-white transition-colors duration-300 group-hover/doc:text-[#F5B301]">
                  {doc.title}
                </p>
                <p className="mt-1 font-tac text-[9px] tracking-[0.25em] text-white/40">{doc.meta}</p>
              </button>
            </Reveal>
          ))}
          {documents.map((doc, i) => {
            const pages = doc.pages.map((p) => `${BACKEND}/api/document-image/${p}`);
            return (
              <Reveal key={doc.id} delay={i * 0.08}>
                <button
                  type="button"
                  data-testid={`doc-dynamic-${doc.id}`}
                  onClick={() => openLightbox(pages)}
                  className="group/doc block w-full text-left"
                >
                  <div className="relative aspect-[3/4] overflow-hidden border border-white/10 bg-white">
                    <img
                      src={pages[0]}
                      alt={`Anonymisiertes Dokument: ${doc.title}`}
                      loading="lazy"
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover/doc:scale-[1.03]"
                    />
                    <span className="absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-all duration-300 group-hover/doc:bg-black/40 group-hover/doc:opacity-100">
                      <Maximize2 className="h-7 w-7 text-[#F5B301]" />
                    </span>
                    <span className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 border-l-2 border-t-2 border-[#F5B301]" />
                    {doc.pages.length > 1 && (
                      <span className="absolute right-2.5 top-2.5 bg-black/70 px-2 py-0.5 font-tac text-[9px] tracking-[0.2em] text-[#F5B301]">
                        {doc.pages.length} SEITEN
                      </span>
                    )}
                  </div>
                  <p className="mt-4 font-display text-lg font-black uppercase tracking-tighter text-white transition-colors duration-300 group-hover/doc:text-[#F5B301]">
                    {doc.title}
                  </p>
                  <p className="mt-1 font-tac text-[9px] tracking-[0.25em] text-white/40">
                    EINSATZ {doc.einsatz_nr}
                  </p>
                </button>
              </Reveal>
            );
          })}
        </div>
      </section>

      <div className="grid border-b border-white/10 lg:grid-cols-3">
        {cards.map((card, i) => (
          <Reveal key={card.id} delay={i * 0.1} className="border-white/10 max-lg:border-b lg:[&:not(:last-child)]:border-r">
            <article data-testid={`referenz-${card.id}`} className="group h-full">
              <div className="relative aspect-[16/10] overflow-hidden">
                <img
                  src={`${BACKEND}/api/showcase-image/${card.image_path}`}
                  alt={card.title}
                  loading="lazy"
                  className="h-full w-full object-cover brightness-[0.85] contrast-110 grayscale-[30%] transition-transform duration-700 ease-out group-hover:scale-105"
                />
                <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_50%_45%,transparent_30%,rgba(9,9,11,0.65)_100%)]" />
                <p className="absolute bottom-3 left-3 font-tac text-[9px] tracking-[0.3em] text-white/60">
                  {card.meta}
                </p>
              </div>
              <div className="p-7 lg:p-9">
                <h3 className="font-display text-2xl font-black uppercase tracking-tighter text-white transition-colors duration-300 group-hover:text-[#F5B301]">
                  {card.title}
                </h3>
                <p className="mt-3 font-tac text-xs leading-relaxed text-[#A1A1AA]">
                  {card.text}
                </p>
              </div>
            </article>
          </Reveal>
        ))}
      </div>

      <div className="px-6 pb-16 pt-14 lg:px-16">
        <Reveal>
          <p className="font-tac text-xs tracking-[0.3em] text-[#F5B301]">EINSATZLOG</p>
        </Reveal>
        <div className="mt-8 border-t border-white/10">
          {missions.map((m) => {
            const mainImg = `${BACKEND}/api/mission-image/${m.image_path}`;
            const all = [mainImg, ...((m.gallery || []).map((g) => `${BACKEND}/api/mission-image/${g.storage_path}`))];
            const cover = m.cover_path ? `${BACKEND}/api/mission-image/${m.cover_path}` : mainImg;
            const images = [cover, ...all.filter((u) => u !== cover)];
            return (
            <Reveal key={m.id}>
              <button
                type="button"
                data-testid={`einsatzlog-dynamic-${m.nr.toLowerCase()}`}
                onClick={() => openLightbox(images, 0)}
                className="group grid w-full grid-cols-12 items-center gap-4 border-b border-white/10 py-4 text-left transition-colors duration-300 hover:bg-white/[0.03]"
              >
                <div className="relative col-span-4 aspect-[16/10] overflow-hidden border border-white/10 sm:col-span-2">
                  <img
                    src={images[0]}
                    alt={m.title}
                    loading="lazy"
                    className="h-full w-full object-cover brightness-[0.8] contrast-110 grayscale-[70%] transition-all duration-500 ease-out group-hover:scale-110 group-hover:grayscale-0"
                  />
                  {images.length > 1 && (
                    <span data-testid={`einsatz-count-${m.nr.toLowerCase()}`} className="absolute bottom-1 right-1 bg-black/70 px-1.5 py-0.5 font-tac text-[9px] tracking-[0.15em] text-[#F5B301]">{images.length} FOTOS</span>
                  )}
                  {m.status && (
                    <span className="absolute left-1.5 top-1.5 flex items-center gap-1 bg-black/75 px-1.5 py-0.5 font-tac text-[8px] tracking-[0.2em] text-[#F5B301]">
                      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#F5B301]" />
                      LÄUFT
                    </span>
                  )}
                </div>
                <span className="col-span-4 font-tac text-xs tracking-[0.2em] text-[#F5B301] sm:col-span-2">
                  {m.nr}
                </span>
                <span className="order-first col-span-12 font-display text-lg font-black uppercase text-white transition-colors duration-300 group-hover:text-[#F5B301] sm:order-none sm:col-span-4 sm:text-xl">
                  {m.title}
                </span>
                <span className="hidden font-tac text-xs text-white/50 lg:col-span-2 lg:block">{m.ort}</span>
                <span className="hidden font-tac text-xs lg:col-span-2 lg:block">
                  {m.status ? <span className="text-[#F5B301]">In Bearbeitung</span> : <span className="text-white/50">{m.datum}</span>}
                </span>
              </button>
            </Reveal>
            );
          })}
        </div>
      </div>

      <div className="flex flex-col gap-6 border-b border-white/10 px-6 py-10 lg:flex-row lg:items-center lg:justify-between lg:px-16">
        <p className="font-tac text-[10px] tracking-[0.3em] text-white/50">
          UNSERE ARBEITSWEISE ALS DOKUMENT:
        </p>
        <div className="flex flex-wrap gap-4">
          <a
            data-testid="referenzen-download-datenblatt"
            href="/assets/datenschutz-datenblatt.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-2.5 border border-white/20 px-5 py-3 font-tac text-[10px] tracking-[0.25em] text-white/80 transition-colors duration-300 hover:border-[#F5B301] hover:text-white"
          >
            <FileDown className="h-4 w-4 text-[#F5B301]" />
            DATENSCHUTZ-DATENBLATT (PDF)
          </a>
          <a
            data-testid="referenzen-download-bildrechte"
            href="/assets/vertrag-bildrechte.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-2.5 border border-white/20 px-5 py-3 font-tac text-[10px] tracking-[0.25em] text-white/80 transition-colors duration-300 hover:border-[#F5B301] hover:text-white"
          >
            <FileDown className="h-4 w-4 text-[#F5B301]" />
            BILDRECHTE-VERTRAG (PDF)
          </a>
        </div>
      </div>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            data-testid="doc-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={() => setLightbox(null)}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-4 backdrop-blur-sm sm:p-10"
          >
            <button
              data-testid="doc-lightbox-close"
              onClick={() => setLightbox(null)}
              aria-label="Großansicht schließen"
              className="absolute right-5 top-5 z-10 flex h-11 w-11 items-center justify-center border border-white/20 text-white transition-colors duration-300 hover:border-[#F5B301] hover:text-[#F5B301]"
            >
              <X className="h-5 w-5" />
            </button>

            {lightbox.images.length > 1 && (
              <>
                <button
                  data-testid="doc-lightbox-prev"
                  onClick={(e) => { e.stopPropagation(); stepLightbox(-1); }}
                  aria-label="Vorherige Seite"
                  className="absolute left-3 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center border border-white/20 bg-black/40 text-white transition-colors duration-300 hover:border-[#F5B301] hover:text-[#F5B301] sm:left-6"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <button
                  data-testid="doc-lightbox-next"
                  onClick={(e) => { e.stopPropagation(); stepLightbox(1); }}
                  aria-label="Nächste Seite"
                  className="absolute right-3 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center border border-white/20 bg-black/40 text-white transition-colors duration-300 hover:border-[#F5B301] hover:text-[#F5B301] sm:right-6"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
                <span
                  data-testid="doc-lightbox-counter"
                  className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2 bg-black/70 px-4 py-1.5 font-tac text-[10px] tracking-[0.3em] text-[#F5B301]"
                >
                  SEITE {lightbox.index + 1} / {lightbox.images.length}
                </span>
              </>
            )}

            <motion.img
              key={lightbox.images[lightbox.index]}
              initial={{ scale: 0.97, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              src={lightbox.images[lightbox.index]}
              alt="Anonymisierte Einsatzdokumentation in Großansicht"
              onClick={(e) => e.stopPropagation()}
              className="max-h-[90vh] max-w-full border border-white/10 object-contain shadow-2xl"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
